import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import {
  useCmsContent,
  useSuspendedQuery,
  useZepApiClient,
  UseZepApiMutationOptions,
  UseZepApiQueryOptions,
} from '@zep/apis';
import { resolveContentId } from '@zep/next';
import { Language } from '@zep/types';

import { StoreBannerItem } from '../components';
import {
  CmsEntries,
  getStoreCategoryIndex,
  StoreCategoryNumber,
  StoreQueryKeys,
} from '../consts';
import { ApiResponsePagerType } from '../types/GlobalTypes';
import {
  AssetStoreDetail,
  AssetStoreReviewList,
  RecommendedHashIdList,
  StoreAppUploadDataType,
  StoreAssetUploadDataType,
  StoreFilterType,
  StoreItem,
  StoreItemType,
  StorePageType,
  StoreReviewDataType,
  StoreUploadResponse,
} from '../types/StoreTypes';
import { SERVER_API } from '../utils';

const getStoreApiUrl = (isEdu?: boolean) =>
  isEdu ? '/v2/edu/store' : '/v2/store';

export function useStoreInquiryBannerList(
  options?: UseZepApiQueryOptions<StoreBannerItem[]>,
) {
  const { locale = 'en' } = useRouter();
  return useCmsContent<StoreBannerItem[]>(
    resolveContentId(CmsEntries.STORE_INQUIRY_BANNERS, locale),
    options,
  );
}

export function useStoreRecommendedHashIds(
  isAsset: boolean,
  isEdu: boolean,
  options?: UseZepApiQueryOptions<RecommendedHashIdList>,
) {
  const { locale = 'en' } = useRouter();
  const contentId = isEdu
    ? isAsset
      ? CmsEntries.STORE_RECOMMEND_ASSETS_EDU
      : CmsEntries.STORE_RECOMMEND_APPS_EDU
    : isAsset
    ? CmsEntries.STORE_RECOMMEND_ASSETS
    : CmsEntries.STORE_RECOMMEND_APPS;

  return useCmsContent<RecommendedHashIdList>(
    resolveContentId(contentId, locale),
    options,
  );
}

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStoreRecommendedItems = (
  type: string,
  assetHashIds: string[] | undefined,
  isEdu: boolean,
  locale: Language,
  options?: UseZepApiQueryOptions<StoreItem[]>,
) => {
  const client = useZepApiClient();
  return useQuery(
    [StoreQueryKeys.RECOMMENDED_LIST, type, isEdu],
    async () => {
      const { data } = await client.post(
        `${getStoreApiUrl(isEdu)}/target-object-map-assets`,
        {
          assetHashIds,
          locale: locale,
        },
      );

      return data.results;
    },

    // @ts-ignore
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStorePopularItems = (
  type: StoreItemType,
  isEdu: boolean,
  locale?: string,
  options?: UseZepApiQueryOptions<StoreItem[]>,
) => {
  const client = useZepApiClient();
  return useQuery(
    [StoreQueryKeys.POPULAR_LIST, type, isEdu],
    async () => {
      const { data } = await client.get(
        `${getStoreApiUrl(isEdu)}/${type}-assets`,
        {
          params: {
            page: 1,
            limit: 12,
            order: 1,
            locale,
          },
        },
      );

      return data.results;
    },

    // @ts-ignore
    options,
  );
};

/**
 * 에셋 리스트를 호출
 * @param type 맵인지 오브젝트인지 체크
 * @param filter? 파라미터로 들어갈 필터(페이지, 정렬, 유/무료, 최신/인기, 공식 앱 여부)
 * @param isEdu? 교융용 계정 여부
 * @param locale
 * @param options
 * @returns 에셋 리스트
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStoreItems = (
  type: string,
  filter: StoreFilterType,
  isEdu: boolean,
  locale?: string,
  options?: UseZepApiQueryOptions<{
    results: StoreItem[];
    pager: ApiResponsePagerType;
  }>,
) => {
  const client = useZepApiClient();
  const order = filter?.order === 'popular' ? 1 : 0;
  filter.limit = filter?.limit ?? 20;

  return useSuspendedQuery(
    [StoreQueryKeys.ASSET_LIST_GET, filter, isEdu, locale],
    async () => {
      const { data } = await client.get(
        `${getStoreApiUrl(isEdu)}/${type}-assets`,
        {
          params: {
            ...filter,
            order,
            locale,
          },
        },
      );

      return { results: data.results, pager: data.pager };
    },

    // @ts-ignore
    options,
  );
};

/**
 * 찜한/구매한 에셋 리스트를 호출하는 API 함수
 * @param type 찜한 에셋인지, 구매한 에셋인지 구별
 * @param isEdu 교융용 계정 여부
 * @param locale
 * @param options
 * @returns 찜한/구매한 에셋 리스트
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStoreSavedItems = (
  type: StorePageType,
  isEdu?: boolean,
  locale?: Language,
  options?: UseZepApiQueryOptions<{
    maps: StoreItem[];
    objects: StoreItem[];
    apps: StoreItem[];
    games: StoreItem[];
  }>,
) => {
  const client = useZepApiClient();

  return useQuery(
    [StoreQueryKeys.ASSET_SAVED_LIST_GET, type],
    async () => {
      const detailPath = type === 'purchased' ? 'purchase' : 'like';
      const { data } = await client.get(
        `${getStoreApiUrl(isEdu)}/${detailPath}-asset`,
        {
          params: { limit: 100, locale },
        },
      );

      return {
        maps: data.items.filter(
          (r: StoreItem) =>
            getStoreCategoryIndex(r.category) === StoreCategoryNumber.map,
        ),
        objects: data.items.filter(
          (r: StoreItem) =>
            getStoreCategoryIndex(r.category) === StoreCategoryNumber.object,
        ),
        apps: data.items.filter(
          (r: StoreItem) =>
            getStoreCategoryIndex(r.category) === StoreCategoryNumber.app,
        ),
        games: data.items.filter(
          (r: StoreItem) =>
            getStoreCategoryIndex(r.category) === StoreCategoryNumber.game,
        ),
      };
    },

    // @ts-ignore
    options,
  );
};

/**
 * 상세 에셋 정보
 * @param assetHashId 에셋의 hash id
 * @param locale 요청 할 언어정보
 * @param isEdu
 * @param options
 * @returns
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStoreItemDetail = (
  assetHashId: string,
  locale?: Language,
  isEdu?: boolean,
  options?: UseZepApiQueryOptions<AssetStoreDetail>,
) => {
  const client = useZepApiClient();

  return useQuery(
    [StoreQueryKeys.ASSET_DETAIL_GET, assetHashId],
    async () => {
      const { data } = await client.get(
        `${getStoreApiUrl(isEdu)}/asset-details`,
        {
          params: {
            assetHashId,
            locale,
          },
        },
      );

      return data;
    },

    // @ts-ignore
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const getAssetDetailServer = (
  assetHashId: string,
  locale?: Language,
  cookie?: string,
  isEdu?: boolean,
) => {
  return SERVER_API.get<AssetStoreDetail>(
    `${getStoreApiUrl(isEdu)}/asset-details`,
    {
      params: {
        assetHashId,
        locale,
      },
      headers: cookie ? { cookie } : undefined,
    },
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStoreEditItemDetail = (
  assetHashId: string,
  options?: UseZepApiQueryOptions<{
    item: StoreItem & StoreAssetUploadDataType & StoreAppUploadDataType;
  }>,
) => {
  const client = useZepApiClient();

  return useQuery(
    [StoreQueryKeys.MY_ASSET_DETAIL_GET, assetHashId],
    async () => {
      const { data } = await client.get(`/v2/store/asset-edit-info`, {
        params: {
          assetHashId,
        },
      });

      return data;
    },

    // @ts-ignore
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useStoreItemReviews = (
  assetHashId: string,
  page: number,
  isEdu?: boolean,
  options?: UseZepApiQueryOptions<AssetStoreReviewList>,
) => {
  const client = useZepApiClient();

  return useQuery(
    [StoreQueryKeys.ASSET_REVIEW_LIST_GET, assetHashId, page],
    async () => {
      const { data } = await client.get(
        `${getStoreApiUrl(isEdu)}/asset-comments`,
        {
          params: {
            assetHashId,
            page,
            limit: 4,
          },
        },
      );

      return data;
    },

    // @ts-ignore
    options,
  );
};

/**
 * 에셋의 리뷰를 등록&수정
 * @param assetHashId 리뷰를 등록할 에셋의 Hash ID
 * @param text 리뷰 텍스트
 * @param rating 리뷰 별점
 * @param isEdu
 * @param options
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePostStoreItemReview = (
  assetHashId: string,
  text: string,
  rating: number,
  isEdu?: boolean,
  options?: UseZepApiMutationOptions<{
    status: number;
    comment: StoreReviewDataType;
  }>,
) => {
  const client = useZepApiClient();

  return useMutation(
    [StoreQueryKeys.ASSET_REVIEW_DETAIL_CREATE],
    async () => {
      const { data } = await client.post(
        `${getStoreApiUrl(isEdu)}/asset-comments`,
        {
          assetHashId,
          text,
          rating,
        },
      );

      return data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const getAssetReviewsServer = (
  assetHashId: string,
  page: number,
  cookie?: string,
  isEdu?: boolean,
) => {
  const REVIEWS_COUNT_PER_PAGE = 4;

  return SERVER_API.get<AssetStoreReviewList>(
    `${getStoreApiUrl(isEdu)}/asset-comments`,
    {
      params: {
        assetHashId,
        page,
        limit: REVIEWS_COUNT_PER_PAGE,
      },
      headers: cookie ? { cookie } : undefined,
    },
  );
};

/**
 * 에셋 데이터를 추가하거나 수정
 * @param type 맵인지 오브젝트인지 여부
 * @param editHashId 수정하는 경우, 에셋의 HashId를 추가
 * @param isEdu
 * @param options
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useUploadStoreItem = (
  type: StoreItemType,
  editHashId: string,
  isEdu: boolean,
  options?: UseZepApiMutationOptions<
    StoreUploadResponse,
    StoreAssetUploadDataType
  >,
) => {
  const client = useZepApiClient();
  const apiUrl = `${getStoreApiUrl(isEdu)}/${type}-assets`;
  const apiConfig = {
    timeout: 100000,
  };

  return useMutation(
    [StoreQueryKeys.ASSET_DETAIL_UPLOAD],
    async formValues => {
      const { data } = editHashId
        ? await client.put(apiUrl, formValues, apiConfig)
        : await client.post(apiUrl, formValues, apiConfig);

      return data;
    },
    options,
  );
};

/**
 * 유저가 가지고 있는 에셋을 삭제하는 API
 * @param assetHashId 삭제할 에셋의 Hash ID
 * @param isEdu
 * @param options
 * @returns isDeleted가 true로 오면 삭제 완료
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useRemoveStoreItem = (
  assetHashId: string,
  isEdu?: boolean,
  options?: UseZepApiMutationOptions<{ status: number; isDeleted: boolean }>,
) => {
  const client = useZepApiClient();

  return useMutation(
    [StoreQueryKeys.ASSET_DETAIL_DELETE],
    async () => {
      const { data } = await client.delete(`${getStoreApiUrl(isEdu)}/asset`, {
        data: {
          assetHashId,
        },
      });

      return data;
    },
    options,
  );
};

/**
 * 특정 에셋의 좋아요 토글
 * @param assetHashId 클릭한 에셋의 Hash ID
 * @param isEdu
 * @param options
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSetLikeStoreItem = (
  assetHashId: string,
  isEdu?: boolean,
  options?: UseZepApiMutationOptions<{ status: number; isLiked: boolean }>,
) => {
  const client = useZepApiClient();

  return useMutation(
    [StoreQueryKeys.LIKE_ASSET],
    async () => {
      const { data } = await client.post(
        `${getStoreApiUrl(isEdu)}/like-asset`,
        {
          assetHashId,
        },
      );

      return data;
    },
    options,
  );
};

/**
 * 특정 에셋을 구매 진행
 * @param assetHashId 클릭한 에셋의 Hash ID
 * @param isEdu
 * @param options
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePurchaseStoreItem = (
  assetHashId: string,
  isEdu?: boolean,
  options?: UseZepApiMutationOptions<{ status: number }>,
) => {
  const client = useZepApiClient();

  return useMutation(
    [StoreQueryKeys.PURCHASE_ASSET],
    async () => {
      const { data } = await client.post(
        `${getStoreApiUrl(isEdu)}/purchase-asset`,
        {
          assetHashId,
        },
      );

      return data;
    },
    options,
  );
};
